// myth-fact-1 — "Debunk Card" (organic, v1 port)
//
// The authority-building debunk format: a confidently wrong belief lands under
// a red MYTH pill, holds, and is then REPLACED by the green FACT correction.
// The red→green flip IN TIME is what carries the format — everything else is
// scaffolding.
//
// Renderdoc port of the v1 MythFactLayer
// (packages/remotion/src/components/myth-fact-layer.tsx):
//   - spine: procedure b-roll, full-bleed, looping, ONE CLIP PER STATEMENT
//     (v1 walks its scenes off the same segment split the cards use; v2 says
//     that as `cuts: 'overlay-synced'`).
//   - spine: a `solid` carrying v1's 4-stop vertical scrim gradient verbatim.
//   - overlay: the optional series title, a `text` pinned top-centre at v1's
//     11% padding, holding for the whole clip.
//   - overlay: ONE `group` at frame centre — a single `repeat` with
//     `reveal: 'sequential'` over the interleaved statement list, alternating
//     the pill copy and the pill colour on `$index % 2`.
//   - overlay: the optional CTA `text`, a dark pill pinned to the master end
//     (`start: 'from-end'`).
//
// ─── THE FLIP, AND HOW `group` GETS IT ──────────────────────────────────────
// The previous port rendered the myth and the fact SIMULTANEOUSLY, in two
// fixed vertical bands, because nothing could interleave two lists in time.
// That was the largest departure from v1 in the whole port: the format's one
// move — belief, beat, correction — was flattened into a static comparison.
//
// `repeat` already sequences: `reveal: 'sequential'` splits the block duration
// evenly across its children and shows exactly one at a time. So the flip needs
// no new primitive — it needs ONE list, interleaved, and a 2-long cycle:
//
//     body = [ myth₀, fact₀, myth₁, fact₁, … ]
//
//     repeat (sequential) ──► $index 0        $index 1       $index 2
//       stack                 pill copy MYTH   pill copy FACT  MYTH
//       ├ text (pill)         frame  RED       frame  GREEN    RED
//       └ stack (card)        $item  myth₀     $item  fact₀     myth₁
//
// One index drives the literal AND the pill Frame, so a statement can never be
// shown under the wrong mark. Segment N unmounts as N+1 enters — v1's Sequence
// behaviour exactly.
//
// ─── DATA: WHAT IS SAFE AND WHAT IS NOT ─────────────────────────────────────
// This template now reads ONE array: script role 'body'.
//
// It used to read two — `list` index 0 (myths) and index 1 (facts) — paired
// positionally with nothing enforcing the alignment. There is no slot-level
// "zip" transform in the model, so two arrays CANNOT be woven into one
// alternating sequence inside a doc; the interleaving has to arrive already
// done.
//
// UNSAFE, HONESTLY: nothing in this file can make the copy layer alternate.
// `discoverScriptSlots` derives the ask from this doc, so Phase A will now ask
// for role 'body' — but with the GENERIC body guidance ("the middle on-screen
// lines that deliver the value"), which produces a flat list of statements,
// not myth/fact pairs. Making this correct requires ONE addition outside this
// package, in
//   packages/features/src/videos/services/synthesize-template/script-prompt.ts
// → `TEMPLATE_ROLE_GUIDANCE`, a new `'myth-fact-1'` entry whose `body` reads
// approximately:
//
//   body: 'An ALTERNATING list of statements, strictly myth-then-fact: item 1
//   is a myth, item 2 is the fact correcting it, item 3 is the next myth, item
//   4 its fact, and so on. Produce an EVEN number of items (4 or 6 — that is
//   2 or 3 pairs). Each myth is a confidently wrong belief the audience
//   actually holds, stated flatly in the audience's own words. Each fact
//   directly corrects the myth immediately before it. Each item ≤10 words,
//   sentence case, no "myth:"/"fact:" prefixes — the template draws the pill.'
//
// Until that lands, the template renders whatever body the model returns, with
// odd-index items labelled FACT regardless of what they say. That is a content
// hazard, not a rendering one, and it is why structurally-PAIRED records (a
// declared content schema, rows as records rather than strings) are the real
// fix — see the `field` content-source seam noted in `script-parsing.ts`. This
// file deliberately does NOT fake pairing.
//
// An ODD item count is also unhandled: the trailing myth gets no fact. Nothing
// in the doc can require an even length (`repeat` has no arity constraint), so
// that too rests on the guidance above.
//
// v1 design source of truth (MythFactLayer):
//   - scrim: linear-gradient(180deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.28) 40%,
//     rgba(0,0,0,0.28) 65%, rgba(0,0,0,0.55) 100%)
//   - series title: 42px / 800 / uppercase / letterSpacing 0.24em / #FFFFFF,
//     textShadow '0 3px 18px rgba(0,0,0,0.55)', paddingTop 11%, fades in over
//     frames [0,10]
//   - state pill: 46px / 800 / uppercase / letterSpacing 0.2em / #FFFFFF on a
//     fully-round fill — MYTH #D64550, FACT #3E8E68, boxShadow
//     '0 8px 26px rgba(0,0,0,0.35)'
//   - statement card: rgba(250,247,242,0.92), radius 28, padding 38/44, width
//     82%, text 52px / 700 / lineHeight 1.28 / REEL_PALETTE.slate (#15161C),
//     boxShadow '0 12px 40px rgba(0,0,0,0.3)'
//   - card group: pill + card stacked with gap 30, spring scale 1.05 → 1 over
//     10 frames (damping 200), fades out over the last 6 frames of its segment
//   - CTA beat: 46px / 800 / #FFFFFF on a fully-round REEL_PALETTE.slateSolid
//     (rgba(13,15,24,0.86)) pill, boxShadow '0 8px 28px rgba(0,0,0,0.4)'
//   - runtime split evenly across (2 × pairs) + (ctaText ? 1 : 0) segments
//
// FIDELITY GAPS vs v1
//
// CLOSED by moving onto `group`:
//   - THE FLIP. One statement at a time, myth replaced by fact, on v1's even
//     segment split — from `repeat` + `reveal: 'sequential'`, no new primitive.
//   - THE RED / GREEN PILLS. A Frame is authored (background, radius 999,
//     padding, shadow), not picked from `pill | button | plate`, and cycles on
//     $index, so MYTH is a filled red pill and FACT a filled green one — v1's
//     colours and shadow verbatim rather than tinted kicker text.
//   - THE CREAM CARD: rgba(250,247,242,0.92) at radius 28 with v1's 38/44
//     padding and its 0 12px 40px shadow, on a `stack` so it spans the full 82%
//     column instead of hugging its copy, and the statement keeps v1's slate
//     #15161C rather than a container-forced #111.
//   - v1's 10-frame entrance (`entranceDurationFrames: 10` on the card group).
//   - THE CARD'S LINE-HEIGHT. v1's statement runs at 1.28 against the engine's
//     1.25 default; `TypeStyleOverride.lineHeight` now states it.
//   - THE CUTS LAND ON THE SWAPS. The b-roll is `cuts: 'overlay-synced'`, whose
//     driver is this doc's own `statements` group (a `repeat` with `reveal:
//     'sequential'` — exactly what `sequentialSegmentLayout` matches). One clip
//     runs per statement, on the SAME floor(blockFrames / N) split the repeat
//     expansion uses, so a cut cannot drift off a card swap. It was
//     `beat-synced` at 4 beats, which only coincided with the swaps when the
//     statement count happened to divide the master the same way.
//
// STILL INEXPRESSIBLE:
//   - PAIRING. See the data note above: two arrays cannot be zipped in a doc,
//     so the pairing lives in prompt guidance and in the model's compliance.
//     GAP FOR THE MODEL: a row source of RECORDS (`over` yielding
//     `{ myth, fact }` with a `field` content source), which would make a pair
//     one row and the alternation a nested 2-repeat — structurally impossible
//     to misalign.
//   - EXITS. v1 fades a card out over the last 6 frames of its segment; a
//     sequential `repeat` child hard-cuts at `exitFrame`. The animation
//     registry has entrances only.
//   - The spring shape (1.05 → 1, damping 200) — `pop` approximates it; v2 owns
//     the spring constants, only the duration is authorable.
//   - `struck` (line-through in MYTH_RED) has no text-decoration knob. v1 never
//     sets it (`struck: false` at both call sites), so nothing is lost today.
//   - THE CTA'S OWN SEGMENT. v1 gives the CTA a final segment with the cards
//     GONE. Here the statement group is `fill` (it must be: the overlay
//     accumulator clamps a shorter overlay to `totalFrames - duration`, which
//     would push the group's START later rather than ending it early), so the
//     last fact is still on screen behind the CTA. It is placed low (y 0.88) to
//     sit under the card. GAP FOR THE MODEL: an overlay `start`/length that can
//     end EARLY at the master end minus N — `from-end` anchors the end, and
//     nothing anchors "stop N frames before the end".
//   - The CTA is a `text` block with `container: 'button'` (#111, radius 14)
//     rather than v1's fully-round rgba(13,15,24,0.86) pill. A one-node `group`
//     could author that Frame; it is left as a `text` so the CTA stays a plain
//     overlay with `start: 'from-end'`.
//   - If the planner supplies no series title, the `text` block resolves to an
//     empty string and still occupies its (empty) slot rather than being
//     omitted the way v1's `config.seriesTitle ? … : null` omits it.
const FPS = 30;
// Fallback length only (music-only template, no narration to drive it): up to 3
// pairs + a CTA. v1's own beat maths (2 segments per pair at ~4 beats each)
// lands in the same 12–14s range.
const TOTAL_FRAMES = 12 * FPS;
// v1 state colours.
const MYTH_RED = '#D64550';
const FACT_GREEN = '#3E8E68';
// v1: the series title ramps in over frames [0,10].
const TITLE_FADE_IN_FRAMES = 10;
// v1 sizes.
const TITLE_FONT_SIZE = 42;
const MARK_FONT_SIZE = 46;
const STATEMENT_FONT_SIZE = 52;
const CTA_FONT_SIZE = 46;
// Legibility shadow for the un-contained title over footage.
const TEXT_SHADOW = '0 3px 18px rgba(0,0,0,0.55)';
// v1 statement card + pill geometry.
const CARD_BACKGROUND = 'rgba(250,247,242,0.92)';
const CARD_RADIUS = 28;
const CARD_PADDING_X = 44;
const CARD_PADDING_Y = 38;
const CARD_SHADOW = '0 12px 40px rgba(0,0,0,0.3)';
const PILL_RADIUS = 999;
const PILL_PADDING_X = 42;
const PILL_PADDING_Y = 12;
const PILL_SHADOW = '0 8px 26px rgba(0,0,0,0.35)';
// v1 REEL_PALETTE.slate — the statement copy on the cream card.
const SLATE = '#15161C';
// v1 stacks pill over card with gap 30 and springs the group in over 10 frames.
const CARD_GROUP_GAP = 30;
const CARD_ENTRANCE_FRAMES = 10;
// The CTA owns the last ~2s of the clip (v1 gives it one segment of its own).
const CTA_FRAMES = 2 * FPS;
export const mythFact1 = {
    id: 'myth-fact-1',
    schemaVersion: 2,
    aspectRatios: ['portrait'],
    duration: { kind: 'fixed', frames: TOTAL_FRAMES },
    root: {
        kind: 'leaf',
        id: 'main',
        spine: [
            // Calm procedure/ambience b-roll under the cards (variation def:
            // recommendedClipCount 3, maxBRollClips 5, filterTag 'procedure').
            {
                kind: 'media-track',
                id: 'broll',
                duration: { kind: 'fill' },
                clips: {
                    source: 'query',
                    query: { kind: 'asset-clips', tag: 'procedure', count: [1, 5] },
                    required: true,
                },
                fit: 'contain-blur',
                // ONE CLIP PER STATEMENT, exactly as v1 does. The `statements` group's
                // root is a `repeat` with `reveal: 'sequential'`, which is precisely
                // what `sequentialSegmentLayout` looks for, so the driver is found and
                // the b-roll cuts land ON the card swaps instead of drifting against
                // them on a fixed 4-beat grid. (`overlay-synced` THROWS when no driver
                // is discovered, so a silent regression here fails the render, not the
                // eye.)
                cuts: { mode: 'overlay-synced' },
                fillMode: 'loop',
            },
            // v1's 4-stop scrim, copied verbatim — dark at the title and the CTA,
            // lifted through the middle band where the card sits.
            {
                kind: 'solid',
                id: 'scrim',
                duration: { kind: 'fill' },
                color: {
                    source: 'fixed',
                    value: 'linear-gradient(180deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.28) 40%, rgba(0,0,0,0.28) 65%, rgba(0,0,0,0.55) 100%)',
                },
            },
        ],
        overlays: [
            // Optional series label ("SKIN MYTHS"), pinned top and held for the whole
            // clip (v1 paddingTop 11%, spaced-out uppercase, fades in over 10 frames).
            {
                kind: 'text',
                id: 'series-title',
                text: {
                    source: 'query',
                    query: { kind: 'script-text', role: 'hook' },
                    required: false,
                },
                style: 'heading',
                styleOverride: {
                    fontSize: TITLE_FONT_SIZE,
                    fontWeight: 800,
                    letterSpacing: 0.24,
                    textTransform: 'uppercase',
                    color: '#FFFFFF',
                    textShadow: TEXT_SHADOW,
                    strokeWidth: 0,
                },
                animation: {
                    entrance: 'fade-in',
                    entranceDurationFrames: TITLE_FADE_IN_FRAMES,
                },
                placement: { anchor: 'top', x: 0.5, y: 0.11, width: 0.88 },
                duration: { kind: 'fill' },
                container: 'none',
            },
            // THE FLIP. One interleaved list, one sequential repeat, one index —
            // myth, then the fact that corrects it, then the next myth.
            {
                kind: 'group',
                id: 'statements',
                duration: { kind: 'fill' },
                // v1 centres the card group and sizes the card to 82%.
                placement: { anchor: 'center', x: 0.5, y: 0.5, width: 0.82 },
                root: {
                    kind: 'repeat',
                    over: {
                        source: 'query',
                        query: { kind: 'script-text', role: 'body' },
                        required: true,
                    },
                    direction: 'v',
                    // One statement on screen at a time; the block duration divides
                    // evenly across the segments, exactly like v1's `per`.
                    reveal: 'sequential',
                    child: {
                        // v1's card group: pill above card, gap 30, springing in together.
                        kind: 'stack',
                        direction: 'v',
                        gap: CARD_GROUP_GAP,
                        align: 'center',
                        entrance: 'pop',
                        entranceDurationFrames: CARD_ENTRANCE_FRAMES,
                        children: [
                            // The state pill. Copy and fill cycle on the SAME $index, so an
                            // even segment is always MYTH-red and an odd one FACT-green.
                            {
                                kind: 'text',
                                content: {
                                    source: 'literal',
                                    text: { byIndex: ['Myth', 'Fact'] },
                                },
                                style: 'heading',
                                styleOverride: {
                                    fontSize: MARK_FONT_SIZE,
                                    fontWeight: 800,
                                    letterSpacing: 0.2,
                                    textTransform: 'uppercase',
                                    color: '#FFFFFF',
                                    strokeWidth: 0,
                                },
                                container: {
                                    byIndex: [
                                        {
                                            background: MYTH_RED,
                                            radius: PILL_RADIUS,
                                            paddingX: PILL_PADDING_X,
                                            paddingY: PILL_PADDING_Y,
                                            shadow: PILL_SHADOW,
                                        },
                                        {
                                            background: FACT_GREEN,
                                            radius: PILL_RADIUS,
                                            paddingX: PILL_PADDING_X,
                                            paddingY: PILL_PADDING_Y,
                                            shadow: PILL_SHADOW,
                                        },
                                    ],
                                },
                            },
                            // The statement card. A stack owns the Frame so the card spans
                            // the full 82% column rather than hugging its copy.
                            {
                                kind: 'stack',
                                direction: 'v',
                                align: 'center',
                                container: {
                                    background: CARD_BACKGROUND,
                                    radius: CARD_RADIUS,
                                    paddingX: CARD_PADDING_X,
                                    paddingY: CARD_PADDING_Y,
                                    shadow: CARD_SHADOW,
                                },
                                children: [
                                    {
                                        kind: 'text',
                                        content: { source: 'item' },
                                        style: 'heading',
                                        styleOverride: {
                                            fontSize: STATEMENT_FONT_SIZE,
                                            fontWeight: 700,
                                            letterSpacing: 0,
                                            textTransform: 'none',
                                            color: SLATE,
                                            // v1 statement span.
                                            lineHeight: 1.28,
                                            strokeWidth: 0,
                                        },
                                        align: 'center',
                                    },
                                ],
                            },
                        ],
                    },
                },
            },
            // Optional closing CTA — v1's slate pill, pinned so it FINISHES with the
            // clip (v1 gives it the final segment). `from-end` keeps it out of the
            // sequential accumulator so the `fill` statement group doesn't push it
            // off the end of the master.
            {
                kind: 'text',
                id: 'cta',
                text: {
                    source: 'query',
                    query: { kind: 'script-text', role: 'cta' },
                    required: false,
                },
                style: 'heading',
                styleOverride: {
                    fontSize: CTA_FONT_SIZE,
                    fontWeight: 800,
                    letterSpacing: 0,
                    textTransform: 'none',
                },
                animation: { entrance: 'pop' },
                // Low enough to clear the statement card, which is still on screen
                // (see FIDELITY GAPS).
                placement: { anchor: 'bottom', x: 0.5, y: 0.88, width: 0.84 },
                duration: { kind: 'fixed', frames: CTA_FRAMES },
                start: 'from-end',
                container: 'button',
            },
        ],
    },
    globals: {
        audio: {
            // Music only — no narration (v1 narrationMode: text_only,
            // supportsAiVoiceover: false).
            music: { source: 'query', query: { kind: 'music' }, required: false },
        },
        // No captions: the cards carry the message.
    },
};
//# sourceMappingURL=myth-fact-1.js.map